import 'react-toastify/dist/ReactToastify.min.css';
import '../index.css';

import { Navigate, Route, Routes } from 'react-router';
import React, { ComponentType, FC, ReactElement, useEffect } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import {
  AVATARS_CALL_ROUTE,
  AVATARS_CREATE_PERSONA_ROUTE,
  AVATARS_EDIT_PERSONA_ROUTE,
  AVATARS_EMBED_ROUTE,
  CALL_PERSONA_ROUTE,
  CALL_WITH_GROUND_CONTROLS_ROUTE,
  CONVERSATIONS_ROUTE,
  CREATE_PERSONA_ROUTE,
  EDIT_PERSONA_ROUTE,
  EMBED_ROUTE,
  ENVIRONMENT,
  EVENT2_ROUTE,
  EVENT_ROUTE,
  featureFlags,
  IAvatar,
  IVoice,
  MAIN_ROUTE,
  PRECONVERSATIONS_LIST,
  TASKS_LIST_ROUTE,
  TAVUS_EMBED_ROUTE,
  useQueryParams,
  VIDEO_FEEDBACK_ROUTE
} from '../shared';

import { EmbedModal } from 'features/embed-persona';

import { UploadCustomPhoto } from 'widgets/upload-custom-photo/ui/UploadCustomPhoto';
import { ShareModal } from 'widgets/share-modal';

import { CreatePersonaPage } from 'pages/create-persona';
import { Dashboard } from 'pages/dashboard';
import { EditPersona } from 'pages/edit-persona';
import { Call } from 'pages/call';
import { CallEmbed } from 'pages/embed/ui/CallEmbed';
import { ToastContainer } from 'react-toastify';
import { HiOutlineX } from 'react-icons/hi';
import { creditStore, features } from 'entities/credit';
import { userStore } from 'entities/user';
import { CallEvent, CallEvent2 } from 'pages/event';
import { AuthLoading } from './AuthLoading';
import { replicaStore } from 'entities/tavus/replica/model/replicaStore';
import { TavusCall } from 'pages/tavus/call/ui';
import { TavusCallEmbed } from 'pages/embedTavus';
import { TavusEmbedModal } from 'features/tavus-embed-persona';
import { TavusCreatePersonaPage } from 'pages/tavus/create-persona';
import { ChooseCreateAgentModal } from 'widgets/choose-create-agent';
import { AvatarEditPersona } from 'pages/avatar-edit-persona';
import { TAvatarShareModal } from 'widgets/t-avatar-share-modal';
import { PreconversationModal } from 'widgets/preconversation-modal';
import { PreconversationsList } from 'pages/preconversations-list';
import { CallWithGroundControls } from 'pages/callWithGroundControls';
import { TasksPage } from 'pages/tasks/ui';
import { VideoFeedback } from 'pages/video-feedback';
import { ConversationsPage } from 'pages/conversations';
import { ViewTranscriptionsModal } from 'features/view-transcriptions-modal';

export type AppProps = {
  updateCallStore: (key: string, value: any) => void;
  toggleUpgradeModal: (isOpened: boolean) => void;
  toggleBuyCreditsModal: (isOpened: boolean) => void;
  toggleFeatureBlockModal: (open: boolean | typeof features[number]) => void;
  toggleUploadAvatar: () => void;
  toggleCreateAccModal: () => void;
  talkingPhotos: IAvatar[];
  customTalkingPhotos: IAvatar[];
  isChatAvailable: boolean;
  premiumVoicesAllowed: boolean;
  selectVoice: FC<{
    currentAvatar: IAvatar | null;
    currentVoice: IVoice | null;
    open: boolean;
    setOpen: (open: boolean) => void;
    chooseVoice: (value: IVoice) => void;
  }>;
  apiKey: string;
  agentKey: string;
  productName: string;
};

const App: FC<AppProps> = (props) => {
  const { me } = userStore();
  const { fetchReplicas } = replicaStore();
  const { isTAvatarsEnabled } = creditStore();
  const query = useQueryParams();
  useEffect(() => {
    me().finally(() => {
      const key = query.get('key') || undefined;
      if (isTAvatarsEnabled() || key) {
        fetchReplicas(key);
      }
    });
  }, []);

  return (
    <>
      <EmbedModal />
      <TavusEmbedModal />
      <ShareModal />
      <TAvatarShareModal />
      <UploadCustomPhoto />
      <ChooseCreateAgentModal />
      <PreconversationModal />
      <ViewTranscriptionsModal />
      <Routes>
        <Route element={<AuthLoading {...props} />}>
          <Route path={MAIN_ROUTE} element={<Dashboard />} />
          <Route path={EDIT_PERSONA_ROUTE} element={<EditPersona />} />
          <Route path={CALL_PERSONA_ROUTE} element={<Call />} />
          <Route path={CREATE_PERSONA_ROUTE} element={<CreatePersonaPage />} />
          <Route path={EMBED_ROUTE} element={<CallEmbed />} />
          <Route path={EVENT_ROUTE} element={<CallEvent />} />
          <Route path={EVENT2_ROUTE} element={<CallEvent2 />} />
          <Route path={TASKS_LIST_ROUTE} element={<TasksPage />} />
          <Route path={VIDEO_FEEDBACK_ROUTE} element={<VideoFeedback />} />
          <Route path={CONVERSATIONS_ROUTE} element={<ConversationsPage />} />
          {featureFlags.conversationTimer && (
            <>
              <Route
                path={PRECONVERSATIONS_LIST}
                element={<PreconversationsList />}
              />
              <Route
                path={CALL_WITH_GROUND_CONTROLS_ROUTE}
                element={<CallWithGroundControls />}
              />
            </>
          )}

          {featureFlags.tAvatars && (
            <>
              <Route path={AVATARS_EMBED_ROUTE} element={<TavusCallEmbed />} />
              <Route path={AVATARS_CALL_ROUTE} element={<TavusCall />} />
              <Route
                path={AVATARS_EDIT_PERSONA_ROUTE}
                element={<AvatarEditPersona />}
              />
              <Route
                path={AVATARS_CREATE_PERSONA_ROUTE}
                element={<TavusCreatePersonaPage />}
              />
            </>
          )}

          {ENVIRONMENT !== 'beta' && ENVIRONMENT !== 'production' && (
            <>
              <Route path={TAVUS_EMBED_ROUTE} element={<TavusCallEmbed />} />
            </>
          )}

          <Route path="*" element={<Navigate to={MAIN_ROUTE} />} />
        </Route>
      </Routes>
      <ToastContainer
        className="ag-z-[9999999]"
        closeButton={({ closeToast }) => (
          <button type="button" onClick={closeToast}>
            <HiOutlineX
              className="ag-size-5 ag-text-neutral-400 hover:ag-text-neutral-500"
              aria-hidden="true"
            />
          </button>
        )}
        hideProgressBar
      />
    </>
  );
};

export default App;
